/**
 * AUIGrid 헤더 경계 더블클릭 → 컬럼 자동 너비 조절
 *
 * 동작:
 *   - 더블클릭: 해당 컬럼만 데이터 기반 자동 너비 조절
 *   - Shift + 더블클릭: 전체 컬럼 데이터 기반 자동 너비 조절
 *   - 헤더 경계선 위에서만 가이드 툴팁 표시
 */

const BORDER_THRESHOLD = 6; // 경계선 판정 거리(px)

export function setupColumnAutoFit(gridInstance, containerSelector) {
  setTimeout(() => {
    try {
      const el = document.querySelector(containerSelector);
      if (!el) {
        console.warn('[AutoFit] container not found:', containerSelector);
        return;
      }

      console.log('[AutoFit] setup OK:', containerSelector);

      // 이전 핸들러 제거
      if (el._af) {
        el.removeEventListener('mousedown', el._af.md, true);
        el.removeEventListener('dblclick', el._af.dbl, true);
        el.removeEventListener('dblclick', el._af.dbl, false);
        el.removeEventListener('mousemove', el._af.mm);
        el.removeEventListener('mouseleave', el._af.ml);
        if (el._af.tip) el._af.tip.remove();
      }

      // 더블클릭 감지 상태
      let lastTime = 0, lastX = 0;

      // 헤더 경계에서 컬럼 찾기 (DOM 기반)
      const findColAtBorder = (clientX) => {
        return findHeaderColumnByDom(gridInstance, el, clientX);
      };

      // 🔧 FIX: 헤더 패널 실제 높이 사용 (하드코딩 50px → DOM 기반)
      // headerHeight: 26, rowHeight: 22일 때 첫 데이터 행(y:26~48)이 50px 안에 들어가
      // 더블클릭 시 AutoFit이 가로채서 셀 편집이 안 되는 문제 수정
      const getHeaderBottom = () => {
        const headerPanel = el.querySelector('.aui-grid-header-panel');
        if (headerPanel) return headerPanel.getBoundingClientRect().bottom - el.getBoundingClientRect().top;
        return 30; // fallback
      };

      // mousedown capture → 더블클릭 타이밍 감지
      const md = (e) => {
        try {
          if (!window.AUIGrid?.isCreated(gridInstance)) return;
          const relY = e.clientY - el.getBoundingClientRect().top;
          const headerH = getHeaderBottom();
          if (relY > headerH) { lastTime = 0; return; }

          const now = Date.now();
          if (now - lastTime < 500 && Math.abs(e.clientX - lastX) < 10) {
            lastTime = 0;
            if (e.shiftKey) {
              autoFitAllColumns(gridInstance);
            } else {
              const col = findColAtBorder(e.clientX);
              if (col) autoFitSingleColumn(gridInstance, col);
            }
          } else {
            lastTime = now;
            lastX = e.clientX;
          }
        } catch (err) { console.warn('[AutoFit] md err:', err.message); }
      };

      // dblclick fallback
      const dbl = (e) => {
        try {
          if (!window.AUIGrid?.isCreated(gridInstance)) return;
          const relY = e.clientY - el.getBoundingClientRect().top;
          const headerH = getHeaderBottom();
          if (relY > headerH) return;
          if (e.shiftKey) {
            autoFitAllColumns(gridInstance);
          } else {
            const col = findColAtBorder(e.clientX);
            if (col) autoFitSingleColumn(gridInstance, col);
          }
        } catch (err) { console.warn('[AutoFit] dbl err:', err.message); }
      };

      // 가이드 툴팁 (경계선 위에서만)
      let tip = null;
      const mm = (e) => {
        const relY = e.clientY - el.getBoundingClientRect().top;
        const onBorder = relY > 0 && relY <= 40 && isNearHeaderBorder(el, e.clientX);
        if (onBorder) {
          if (!tip) {
            tip = document.createElement('div');
            tip.style.cssText =
              'position:fixed;z-index:99999;padding:3px 8px;border-radius:3px;' +
              'background:#333;color:#fff;font-size:11px;pointer-events:none;' +
              'white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);';
            tip.textContent = '더블클릭: 컬럼 자동 너비 | Shift+더블클릭: 전체';
            document.body.appendChild(tip);
          }
          tip.style.left = (e.clientX + 12) + 'px';
          tip.style.top = (e.clientY + 16) + 'px';
          tip.style.display = 'block';
        } else if (tip) {
          tip.style.display = 'none';
        }
      };

      const ml = () => { if (tip) tip.style.display = 'none'; };

      el._af = { md, dbl, mm, ml, tip: null };
      Object.defineProperty(el._af, 'tip', { get: () => tip });

      el.addEventListener('mousedown', md, true);
      el.addEventListener('dblclick', dbl, true);
      el.addEventListener('dblclick', dbl, false);
      el.addEventListener('mousemove', mm);
      el.addEventListener('mouseleave', ml);

    } catch (e) {
      console.warn('[AutoFit] setup error:', e.message);
    }
  }, 800);
}

// ─── 내부 함수들 ────────────────────────────────────

/** 텍스트 픽셀 너비 측정 (Canvas) */
let _cvs = null;
function textW(text, font) {
  if (!_cvs) _cvs = document.createElement('canvas');
  const ctx = _cvs.getContext('2d');
  ctx.font = font || '12px "Malgun Gothic", sans-serif';
  return ctx.measureText(String(text ?? '')).width;
}

/** 헤더 <td> 목록 가져오기 (좌측 고정 + 스크롤 패널 모두) */
function getHeaderTds(el) {
  return el.querySelectorAll('.aui-grid-header-panel .aui-grid-table td');
}

/** clientX가 헤더 <td>의 오른쪽 경계선 근처인지 */
function isNearHeaderBorder(el, clientX) {
  const tds = getHeaderTds(el);
  for (const td of tds) {
    if (Math.abs(clientX - td.getBoundingClientRect().right) <= BORDER_THRESHOLD) {
      return true;
    }
  }
  return false;
}

/**
 * DOM 기반 컬럼 찾기:
 * 1) 모든 헤더 <td>의 실제 렌더 위치(getBoundingClientRect)로 경계 판정
 * 2) <td>의 텍스트 → colInfos의 headerText 매칭
 */
function findHeaderColumnByDom(gridInstance, el, clientX) {
  const colInfos = window.AUIGrid.getColumnInfoList(gridInstance);
  if (!colInfos?.length) return null;

  const tds = getHeaderTds(el);
  if (!tds?.length) return null;

  // 1) 오른쪽 경계에 가장 가까운 td 찾기
  let bestTd = null;
  let bestDist = Infinity;
  for (const td of tds) {
    const dist = Math.abs(clientX - td.getBoundingClientRect().right);
    if (dist < bestDist) {
      bestDist = dist;
      bestTd = td;
    }
  }

  // 경계선이 아니면 → 셀 내부 클릭 → 해당 셀
  if (!bestTd || bestDist > BORDER_THRESHOLD) {
    for (const td of tds) {
      const r = td.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) {
        bestTd = td;
        break;
      }
    }
  }
  if (!bestTd) return null;

  // 2) td의 텍스트 → colInfo 매칭
  const cellText = (bestTd.textContent || '').trim();
  console.log('[AutoFit] clicked td text:', JSON.stringify(cellText));

  // 정확 매칭
  let match = colInfos.find(c => c.headerText === cellText);
  if (match) return match;

  // 부분 매칭 (필터 아이콘 등으로 텍스트 앞뒤 차이)
  match = colInfos.find(c =>
    c.headerText && cellText &&
    (cellText.includes(c.headerText) || c.headerText.includes(cellText))
  );
  if (match) return match;

  // dataField 매칭
  match = colInfos.find(c => c.dataField === cellText);
  console.warn('[AutoFit] text match failed, cellText:', cellText);
  return match || null;
}

/** 개별 컬럼 최적 너비 계산 + 적용 (값 기준) */
function autoFitSingleColumn(gridInstance, colInfo) {
  const field = colInfo.dataField;
  if (!field) return;

  const optimalW = calcDataBasedWidth(gridInstance, colInfo);

  console.log('[AutoFit]', field, ':', colInfo.width, '→', optimalW);
  window.AUIGrid.setColumnPropByDataField(gridInstance, field, { width: optimalW });
  try { window.AUIGrid.resize(gridInstance); } catch (e) {}
}

/**
 * 값(데이터) 기준 최적 너비 계산
 * - 데이터가 있는 컬럼: 가장 긴 데이터 값 기준
 * - 데이터가 없는 컬럼: 최소 너비 (헤더 축약)
 */
function calcDataBasedWidth(gridInstance, colInfo) {
  const field = colInfo.dataField;
  const gridData = window.AUIGrid.getGridData(gridInstance) || [];
  const PADDING = 24;
  const MIN_W = 30;       // 값 없을 때 최소 너비
  const HEADER_MIN = 36;  // 헤더만 보일 때 최소

  // 데이터 셀 중 가장 넓은 값 찾기
  let maxDataW = 0;
  let hasData = false;
  for (let i = 0; i < gridData.length; i++) {
    const val = gridData[i]?.[field];
    if (val !== undefined && val !== null && val !== '') {
      hasData = true;
      const w = textW(val) + PADDING;
      if (w > maxDataW) maxDataW = w;
    }
  }

  if (hasData) {
    // 데이터 기준 너비 (헤더보다 작아도 OK)
    return Math.max(Math.ceil(maxDataW), MIN_W);
  } else {
    // 데이터 없음 → 최소화
    return HEADER_MIN;
  }
}

/** Shift+더블클릭 또는 초기 로드: 전체 컬럼 자동 너비 (값 기준) */
export function autoFitAllColumns(gridInstance) {
  try {
    const colInfos = window.AUIGrid.getColumnInfoList(gridInstance);
    if (!colInfos?.length) return;

    console.log('[AutoFit] Shift+더블클릭 → 전체 컬럼 자동 너비 (값 기준)');

    colInfos.forEach((col) => {
      const field = col.dataField;
      if (!field || field === '_rownum') return;

      const optimalW = calcDataBasedWidth(gridInstance, col);
      if (optimalW !== col.width) {
        window.AUIGrid.setColumnPropByDataField(gridInstance, field, { width: optimalW });
      }
    });

    try { window.AUIGrid.resize(gridInstance); } catch (e) {}
    console.log('[AutoFit] 전체 컬럼 자동 너비 완료');
  } catch (err) {
    console.warn('[AutoFit] autoFitAll error:', err.message);
  }
}
